import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Facebook, Instagram, Twitter, Youtube, Linkedin, Link2,
  MessageCircle, Code2, Image, Globe, Save, BarChart3, CheckCircle2, Info
} from 'lucide-react';
import { toast } from 'sonner';

// TikTok icon (not in lucide)
function TikTokIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.28 6.28 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34l-.01-8.43a8.18 8.18 0 0 0 4.78 1.53V5.01a4.85 4.85 0 0 1-1-.32z" />
    </svg>
  );
}

const SOCIAL_FIELDS = [
  { key: 'social_facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/username', color: 'text-blue-600' },
  { key: 'social_instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/username', color: 'text-pink-500' },
  { key: 'social_twitter', label: 'X / Twitter', icon: Twitter, placeholder: 'https://twitter.com/username', color: 'text-sky-500' },
  { key: 'social_tiktok', label: 'TikTok', icon: TikTokIcon, placeholder: 'https://tiktok.com/@username', color: 'text-slate-800' },
  { key: 'social_youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@channel', color: 'text-red-500' },
  { key: 'social_linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/username', color: 'text-blue-700' },
];

const DEFAULT_EXTRAS = {
  social_facebook: '',
  social_instagram: '',
  social_twitter: '',
  social_tiktok: '',
  social_youtube: '',
  social_linkedin: '',
  wa_widget_enabled: false,
  wa_widget_number: '',
  wa_widget_message: 'Halo, saya ingin bertanya tentang produk Anda.',
  custom_js: '',
  favicon_url: '',
  fb_pixel_id: '',
  tiktok_pixel_id: '',
  gtm_id: '',
};

export default function StoreExtras() {
  const [extras, setExtras] = useState(DEFAULT_EXTRAS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('social');

  const fetchExtras = async () => {
    setLoading(true);
    try {
      const res = await api.get('/tenant/store-extras');
      setExtras({ ...DEFAULT_EXTRAS, ...(res.data.extras || {}) });
    } catch { toast.error('Gagal memuat pengaturan'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchExtras(); }, []);

  const set = (key, val) => setExtras(e => ({ ...e, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/store-extras', extras);
      toast.success('Pengaturan berhasil disimpan');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" />
    </div>
  );

  const activeSocials = SOCIAL_FIELDS.filter(f => extras[f.key]).length;

  return (
    <div className="space-y-6" data-testid="store-extras-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Pengaturan Toko Lanjutan</h1>
          <p className="text-sm text-slate-500 mt-1">Social media, widget WhatsApp, custom script & favicon</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
          <Save size={16} className="mr-2" />
          {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="social">
            <Link2 size={14} className="mr-2" /> Social Media
            {activeSocials > 0 && <span className="ml-2 bg-[#2D6CDF] text-white text-xs rounded-full px-1.5 py-0.5">{activeSocials}</span>}
          </TabsTrigger>
          <TabsTrigger value="whatsapp">
            <MessageCircle size={14} className="mr-2" /> WhatsApp Widget
          </TabsTrigger>
          <TabsTrigger value="custom">
            <Code2 size={14} className="mr-2" /> Custom JS
          </TabsTrigger>
          <TabsTrigger value="favicon">
            <Image size={14} className="mr-2" /> Favicon
          </TabsTrigger>
          <TabsTrigger value="tracking">
            <BarChart3 size={14} className="mr-2" /> Tracking & Pixel
          </TabsTrigger>
        </TabsList>

        {/* ── Social Media ── */}
        <TabsContent value="social">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Link2 size={18} className="text-[#2D6CDF]" /> Tautan Social Media
              </CardTitle>
              <p className="text-sm text-slate-500">Link akan tampil di footer dan halaman kontak toko Anda</p>
            </CardHeader>
            <CardContent className="space-y-5">
              {SOCIAL_FIELDS.map(({ key, label, icon: Icon, placeholder, color }) => (
                <div key={key} className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 ${color}`}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500 mb-1">{label}</Label>
                    <Input
                      value={extras[key] || ''}
                      onChange={e => set(key, e.target.value)}
                      placeholder={placeholder}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── WhatsApp Widget ── */}
        <TabsContent value="whatsapp">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageCircle size={18} className="text-emerald-500" /> Floating WhatsApp Widget
              </CardTitle>
              <p className="text-sm text-slate-500">Tombol WhatsApp mengambang di pojok bawah halaman toko</p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <div>
                  <p className="font-medium text-slate-800">Aktifkan Widget WhatsApp</p>
                  <p className="text-sm text-slate-500 mt-0.5">Pelanggan bisa langsung chat dengan Anda</p>
                </div>
                <Switch
                  checked={!!extras.wa_widget_enabled}
                  onCheckedChange={v => set('wa_widget_enabled', v)}
                />
              </div>

              <div className={!extras.wa_widget_enabled ? 'opacity-50 pointer-events-none' : ''}>
                <div className="space-y-4">
                  <div>
                    <Label>Nomor WhatsApp <span className="text-red-500">*</span></Label>
                    <div className="flex mt-1">
                      <span className="flex items-center px-3 bg-slate-100 border border-r-0 border-slate-200 rounded-l-md text-sm text-slate-500">+62</span>
                      <Input
                        value={extras.wa_widget_number || ''}
                        onChange={e => set('wa_widget_number', e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="8123456789"
                        className="rounded-l-none"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Tanpa angka 0 di depan. Contoh: 8123456789</p>
                  </div>
                  <div>
                    <Label>Pesan Otomatis</Label>
                    <Textarea
                      value={extras.wa_widget_message || ''}
                      onChange={e => set('wa_widget_message', e.target.value)}
                      rows={3}
                      placeholder="Halo, saya ingin bertanya tentang produk Anda."
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-400 mt-1">Pesan ini akan terisi otomatis saat pelanggan membuka WhatsApp</p>
                  </div>
                </div>
              </div>

              {/* Preview */}
              {extras.wa_widget_enabled && extras.wa_widget_number && (
                <div className="mt-4 p-4 bg-slate-800 rounded-xl relative overflow-hidden">
                  <p className="text-xs text-slate-400 mb-3">Preview Widget</p>
                  <div className="flex justify-end">
                    <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center shadow-xl cursor-pointer hover:scale-105 transition-transform">
                      <MessageCircle size={28} className="text-white" />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Custom JS ── */}
        <TabsContent value="custom">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Code2 size={18} className="text-violet-500" /> Custom JavaScript
              </CardTitle>
              <p className="text-sm text-slate-500">Script akan diinjeksi di bagian bawah halaman toko (&lt;/body&gt;). Cocok untuk Google Analytics, Meta Pixel, atau widget pihak ketiga.</p>
            </CardHeader>
            <CardContent>
              <div className="rounded-xl overflow-hidden border border-slate-200">
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 border-b border-slate-700">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-amber-400" />
                    <div className="w-3 h-3 rounded-full bg-emerald-400" />
                  </div>
                  <span className="text-xs text-slate-400 ml-2">custom.js</span>
                </div>
                <Textarea
                  value={extras.custom_js || ''}
                  onChange={e => set('custom_js', e.target.value)}
                  rows={16}
                  placeholder={'// Contoh: Google Analytics\n// <script async src="https://www.googletagmanager.com/gtag/js?id=GA_ID"></script>\n\n// Contoh: Meta Pixel\n// fbq(\'init\', \'PIXEL_ID\');\n// fbq(\'track\', \'PageView\');'}
                  className="font-mono text-sm border-0 rounded-none bg-slate-900 text-emerald-300 resize-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-slate-600"
                  spellCheck={false}
                />
              </div>
              <p className="text-xs text-amber-600 mt-3 flex items-start gap-1.5">
                <span className="shrink-0 mt-0.5">⚠️</span>
                Hanya masukkan script dari sumber terpercaya. Script yang berbahaya dapat merusak toko Anda.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Favicon ── */}
        <TabsContent value="favicon">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Image size={18} className="text-amber-500" /> Favicon
              </CardTitle>
              <p className="text-sm text-slate-500">Ikon kecil yang tampil di tab browser. Disarankan ukuran 32×32 atau 64×64 px format PNG atau ICO.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-start gap-6">
                {/* Preview */}
                <div className="shrink-0">
                  <p className="text-xs text-slate-500 mb-2">Preview</p>
                  <div className="w-16 h-16 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center bg-slate-50 overflow-hidden">
                    {extras.favicon_url ? (
                      <img
                        src={extras.favicon_url}
                        alt="Favicon"
                        className="w-10 h-10 object-contain"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <Globe size={24} className="text-slate-300" />
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-1 px-2 py-1 bg-slate-200 rounded text-xs text-slate-600 max-w-[90px] overflow-hidden">
                    {extras.favicon_url ? (
                      <img src={extras.favicon_url} alt="" className="w-4 h-4 object-contain shrink-0" onError={e => e.target.style.display = 'none'} />
                    ) : (
                      <Globe size={12} className="text-slate-400 shrink-0" />
                    )}
                    <span className="truncate">Toko Saya</span>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1 text-center">Tab browser</p>
                </div>

                {/* Input */}
                <div className="flex-1 space-y-3">
                  <div>
                    <Label>URL Favicon</Label>
                    <Input
                      value={extras.favicon_url || ''}
                      onChange={e => set('favicon_url', e.target.value)}
                      placeholder="https://cdn.example.com/favicon.ico"
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-400 mt-1">Masukkan URL gambar yang sudah di-upload ke CDN atau hosting</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs font-medium text-blue-800 mb-1">💡 Tips Upload Favicon</p>
                    <ul className="text-xs text-blue-700 space-y-0.5">
                      <li>• Upload ke Google Drive, Imgur, atau Cloudinary</li>
                      <li>• Gunakan gambar berukuran 32×32 atau 64×64 px</li>
                      <li>• Format yang didukung: PNG, ICO, SVG, GIF</li>
                      <li>• Pastikan URL bisa diakses publik</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        {/* ── Tracking & Pixel ── */}
        <TabsContent value="tracking">
          <div className="space-y-6">
            {/* Info banner */}
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 flex items-start gap-3">
              <Info size={18} className="text-blue-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">Tracking Pixel Otomatis</p>
                <p className="text-xs text-blue-600 mt-0.5">Cukup masukkan ID pixel, script akan otomatis diinjeksi di semua halaman toko. Event yang dilacak: PageView, ViewContent, AddToCart, InitiateCheckout, dan Purchase.</p>
              </div>
            </div>

            {/* Facebook Pixel */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Facebook size={18} className="text-blue-600" />
                  </div>
                  Facebook Pixel (Meta Pixel)
                  {extras.fb_pixel_id && <span className="ml-auto flex items-center gap-1 text-xs text-emerald-600 font-normal"><CheckCircle2 size={14} /> Aktif</span>}
                </CardTitle>
                <p className="text-sm text-slate-500">Lacak konversi, buat audience, dan optimalkan iklan Facebook & Instagram Anda</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Pixel ID</Label>
                  <Input
                    value={extras.fb_pixel_id || ''}
                    onChange={e => set('fb_pixel_id', e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="Contoh: 123456789012345"
                    className="mt-1 font-mono"
                    maxLength={20}
                  />
                  <p className="text-xs text-slate-400 mt-1">Dapatkan Pixel ID di Facebook Events Manager → Data Sources</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="text-xs font-medium text-slate-700 mb-2">Event yang dilacak:</p>
                  <div className="flex flex-wrap gap-2">
                    {['PageView', 'ViewContent', 'AddToCart', 'InitiateCheckout', 'Purchase'].map(ev => (
                      <span key={ev} className="px-2 py-1 bg-white border rounded text-xs text-slate-600">{ev}</span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* TikTok Pixel */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                    <TikTokIcon size={18} />
                  </div>
                  TikTok Pixel
                  {extras.tiktok_pixel_id && <span className="ml-auto flex items-center gap-1 text-xs text-emerald-600 font-normal"><CheckCircle2 size={14} /> Aktif</span>}
                </CardTitle>
                <p className="text-sm text-slate-500">Lacak performa iklan TikTok dan buat Custom Audience</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Pixel ID</Label>
                  <Input
                    value={extras.tiktok_pixel_id || ''}
                    onChange={e => set('tiktok_pixel_id', e.target.value.replace(/[^A-Za-z0-9]/g, ''))}
                    placeholder="Contoh: C5ABCDE1234FGHIJ567"
                    className="mt-1 font-mono"
                    maxLength={30}
                  />
                  <p className="text-xs text-slate-400 mt-1">Dapatkan Pixel ID di TikTok Ads Manager → Events → Web Events</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="text-xs font-medium text-slate-700 mb-2">Event yang dilacak:</p>
                  <div className="flex flex-wrap gap-2">
                    {['PageView', 'ViewContent', 'AddToCart', 'InitiateCheckout', 'CompletePayment'].map(ev => (
                      <span key={ev} className="px-2 py-1 bg-white border rounded text-xs text-slate-600">{ev}</span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Google Tag Manager */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <BarChart3 size={18} className="text-amber-600" />
                  </div>
                  Google Tag Manager
                  {extras.gtm_id && <span className="ml-auto flex items-center gap-1 text-xs text-emerald-600 font-normal"><CheckCircle2 size={14} /> Aktif</span>}
                </CardTitle>
                <p className="text-sm text-slate-500">Kelola semua tag marketing (Google Analytics, Google Ads, dll) dalam satu tempat</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Container ID</Label>
                  <Input
                    value={extras.gtm_id || ''}
                    onChange={e => set('gtm_id', e.target.value.replace(/[^A-Za-z0-9-]/g, '').toUpperCase())}
                    placeholder="Contoh: GTM-XXXXXXX"
                    className="mt-1 font-mono"
                    maxLength={15}
                  />
                  <p className="text-xs text-slate-400 mt-1">Dapatkan Container ID di tagmanager.google.com → Admin → Container Settings</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="text-xs font-medium text-slate-700 mb-2">Data Layer events yang dikirim:</p>
                  <div className="flex flex-wrap gap-2">
                    {['page_view', 'view_item', 'add_to_cart', 'begin_checkout', 'purchase'].map(ev => (
                      <span key={ev} className="px-2 py-1 bg-white border rounded text-xs text-slate-600 font-mono">{ev}</span>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Buat Trigger di GTM berdasarkan event di atas</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
